<script setup lang="ts">
import { ElDrawer } from 'element-plus'
import { computed, ref } from 'vue'

import { convertSizePixels } from '../helpers/ui'

const props = withDefaults(
  defineProps<{
    direction?: 'rtl' | 'ltr' | 'ttb' | 'btt'
    destroyOnClose?: boolean
    lockScroll?: boolean
    showClose?: boolean
    withHeader?: boolean
    size?: number | string
    resizable?: boolean
    paddingless?: boolean
    fullscreenOnMobile?: boolean
  }>(),
  {
    direction: 'rtl',
    destroyOnClose: true,
    lockScroll: true,
    showClose: true,
    withHeader: false,
    size: '30%',
    resizable: false,
    paddingless: false,
    fullscreenOnMobile: false,
  },
)
const value = defineModel<boolean>('modelValue', { default: false })
const emits = defineEmits(['closed'])
const resizing = ref(false)
const DEFAULT_SIDEBAR_WIDTH = 80

const initialSizeInPixel = computed(() => convertSizePixels(props.size, window.innerWidth))
const size = ref(convertSizePixels(props.size, window.innerWidth))

const onMouseMove = (moveEvent: MouseEvent) => {
  if (!resizing.value) {
    return
  }

  size.value = Math.max(
    initialSizeInPixel.value,
    Math.min(window.innerWidth - DEFAULT_SIDEBAR_WIDTH, window.innerWidth - moveEvent.clientX),
  )
}

const onMouseUp = () => {
  resizing.value = false
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
}

const startResize = () => {
  resizing.value = true
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}
</script>
<template>
  <ElDrawer
    v-bind="props"
    v-model="value"
    :size="size"
    :class="{ drawer__resizing: resizing, drawer__paddingless: paddingless, 'drawer__fullscreen-on-mobile': fullscreenOnMobile }"
    :show-close="showClose"
    @closed="emits('closed')"
  >
    <template v-if="$slots.header" #header><slot name="header" /></template>
    <template #default>
      <slot name="default" />
      <div v-if="resizable" class="resize-handle" @mousedown="startResize" />
    </template>
  </ElDrawer>
</template>
<style lang="scss">
.ElDrawer {
  --ElDrawer-padding-primary: 24px;

  &.drawer__resizing {
    cursor: ew-resize;
    transition: none;
    user-select: none;
  }

  &.rtl {
    padding-top: 72px;
  }
}
.ElDrawer__header {
  margin-bottom: 0;
}

.drawer__paddingless .el-drawer__body {
  padding: 0;
}
@media screen and (max-width: 992px) {
  .drawer__fullscreen-on-mobile {
    width: 100% !important;
  }
}
</style>
<style lang="scss" scoped>
.resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  cursor: ew-resize;
  background: rgba(0, 0, 0, 0.1);
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
}
</style>
